import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Loader } from "@googlemaps/js-api-loader";

console.log("🔍 GooglePlacesLocation.tsx file loaded");

interface GooglePlacesLocationProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  onPlaceSelect?: (place: google.maps.places.PlaceResult) => void;
  onCoordinatesChange?: (coords: { lat: number; lng: number } | null) => void;
}

export const GooglePlacesLocation = ({
  value,
  onChange,
  placeholder = "Enter city, town, or village",
  className,
  id,
  name,
  required,
  onPlaceSelect,
  onCoordinatesChange
}: GooglePlacesLocationProps) => {
  console.log("🚀 GooglePlacesLocation component mounted with props:", { value, placeholder });
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [googleMapsKey, setGoogleMapsKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>(value);

  // Keep input DOM in sync when parent value changes (uncontrolled input)
  useEffect(() => {
    if (typeof value === 'string' && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Fetch Google Maps API key
  useEffect(() => {
    const fetchGoogleMapsKey = async () => {
      try {
        const response = await fetch('https://asrkgtsboqenyuetcjuj.supabase.co/functions/v1/get-google-maps-key', {
          headers: {
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFzcmtndHNib3Flbnl1ZXRjanVqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY4NjY0NjQsImV4cCI6MjA3MjQ0MjQ2NH0.IbmQpxyInbEBlRU_3kd-vdiHmlw7tgbhEk3j7LRpCck`,
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFzcmtndHNib3Flbnl1ZXRjanVqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY4NjY0NjQsImV4cCI6MjA3MjQ0MjQ2NH0.IbmQpxyInbEBlRU_3kd-vdiHmlw7tgbhEk3j7LRpCck'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setGoogleMapsKey(data.key);
        } else {
          console.error('Failed to fetch Google Maps key');
        }
      } catch (error) {
        console.error('Error fetching Google Maps key:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGoogleMapsKey();
  }, []);

  // Store latest callbacks in refs to avoid effect recreation
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const onCoordinatesChangeRef = useRef(onCoordinatesChange);

  // Update refs when callbacks change
  useEffect(() => {
    onChangeRef.current = onChange;
    onPlaceSelectRef.current = onPlaceSelect;
    onCoordinatesChangeRef.current = onCoordinatesChange;
  });

  // Initialize Google Places Autocomplete
  useEffect(() => {
    console.log("🔄 GooglePlaces useEffect triggered", { inputRef: !!inputRef.current, googleMapsKey: !!googleMapsKey, isLoading, autocompleteRef: !!autocompleteRef.current });
    if (!inputRef.current || !googleMapsKey || isLoading) return;
    if (autocompleteRef.current) return;

    const initAutocomplete = async () => {
      console.log("🚀 Starting autocomplete initialization...");
      try {
        const loader = new Loader({
          apiKey: googleMapsKey,
          version: "weekly",
          libraries: ["places"],
          language: "en"
        });

        await loader.load();

        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current!, {
          componentRestrictions: { country: "gb" },
          fields: ["formatted_address", "address_components", "geometry", "name", "place_id"]
        });
        autocomplete.setOptions({ types: ["geocode"] });
        console.log("📍 Autocomplete initialized with types: geocode");

        autocompleteRef.current = autocomplete;

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          console.log("🌍 Google Places location selected:", place);
          
          let locationName = "";
          if (place.formatted_address) {
            locationName = place.formatted_address;
            console.log("✅ Using formatted_address:", locationName);
          } else if (place.name) {
            locationName = place.name;
            console.log("✅ Using place name:", locationName);
          } else if (place.address_components) {
            const locality = place.address_components.find(
              comp => comp.types.includes("locality") || comp.types.includes("postal_town")
            );
            if (locality) {
              locationName = locality.long_name;
              console.log("✅ Using locality:", locationName);
            }
          }
          
          if (locationName) {
            setInputValue(locationName);
            console.log("🔄 Calling onChange with:", locationName);
            onChangeRef.current(locationName);
            onPlaceSelectRef.current?.(place);
            
            // Extract coordinates
            const coords = place.geometry?.location ? {
              lat: typeof place.geometry.location.lat === 'function' 
                ? place.geometry.location.lat() 
                : (place.geometry.location.lat as unknown as number),
              lng: typeof place.geometry.location.lng === 'function' 
                ? place.geometry.location.lng() 
                : (place.geometry.location.lng as unknown as number)
            } : null;
            
            onCoordinatesChangeRef.current?.(coords);
          }
        });

      } catch (error) {
        console.error("Error initializing Google Places Autocomplete:", error);
      }
    };

    initAutocomplete();

    return () => {
      if (autocompleteRef.current && window.google) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [googleMapsKey, isLoading]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    console.log("⌨️ Manual input change:", newValue, "length:", newValue.length);
    setInputValue(newValue);
    onChange(newValue);
  };

  if (isLoading) {
    return (
      <Input
        value=""
        placeholder="Loading location services..."
        disabled
        className={cn(className)}
      />
    );
  }

  if (!googleMapsKey) {
    return (
      <Input
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={cn(className)}
        id={id}
        name={name}
        required={required}
      />
    );
  }

  return (
    <Input
      ref={inputRef}
      value={inputValue}
      onChange={handleInputChange}
      placeholder={placeholder}
      className={cn(className)}
      id={id}
      name={name}
      required={required}
      autoComplete="off"
      maxLength={undefined}
    />
  );
};